/**
 * Checkbox.
 *
 * The real input stays in the DOM and keeps focus, so the keyboard map, the
 * form value and the screen reader announcement are the platform's. The drawn
 * box is a sibling that reads the input's state through CSS.
 *
 * The whole row is the target, and the row grows: an explanation line under a
 * checkbox is normal in this product, and it has to survive 1.4x expansion.
 */

import type { InputHTMLAttributes } from 'react';
import { useId } from 'react';
import '../../styles/components/checkbox.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string;
  /** One line of consequence, in the person's own vocabulary. */
  help?: string;
  id?: string;
}

export function Checkbox({ label, help, id, disabled, ...rest }: CheckboxProps) {
  const generated = useId();
  const controlId = id ?? generated;

  return (
    <div className="check" data-disabled={disabled === true ? 'yes' : 'no'}>
      <input
        {...rest}
        type="checkbox"
        id={controlId}
        className="check__input"
        disabled={disabled}
        aria-describedby={help ? `${controlId}-help` : undefined}
      />
      <span className="check__box" aria-hidden="true">
        <svg viewBox="0 0 16 16" width="10" height="10" focusable="false">
          <path
            d="M3 8.5 6.5 12 13 4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <label className="check__label" htmlFor={controlId}>
        {label}
      </label>
      {help ? (
        <p className="check__help" id={`${controlId}-help`}>
          {help}
        </p>
      ) : null}
    </div>
  );
}
